import React, { useState, useRef, useCallback } from 'react';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string | null) => void;
  label?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange, label }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(async (file: File) => {
    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Upload failed');
      }

      const data = await response.json();
      onChange(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  }, [handleUpload]);

  if (value) {
    return (
      <div className="admin-form-group">
        {label && <label className="admin-form-label">{label}</label>}
        <div className="admin-image-preview">
          <img src={value} alt="Preview" />
          <button
            type="button"
            className="admin-image-preview-remove"
            onClick={() => onChange(null)}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-form-group">
      {label && <label className="admin-form-label">{label}</label>}
      <div
        className={`admin-image-upload ${dragging ? 'dragging' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        {uploading ? (
          <div className="admin-spinner" />
        ) : (
          <>
            <div className="admin-image-upload-icon">📷</div>
            <div className="admin-image-upload-text">
              Drag and drop or click to upload
            </div>
            <div className="admin-image-upload-hint">
              Max 5MB - JPG, PNG, WebP, GIF
            </div>
          </>
        )}
      </div>
      {error && (
        <span className="admin-input-hint" style={{ color: 'var(--admin-error)' }}>
          {error}
        </span>
      )}
    </div>
  );
};

export default ImageUpload;
